import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Scanner } from "@yudiel/react-qr-scanner";
import { getUserData, logout } from "../redux/slices/userSlices";
import Api from "../components/api/api";
import { URL_API } from "../components/constants/api";
import ToastProvider from "../hooks/useToastProvider";
import { CheckinInfo, CheckinResult } from "../utils/interface.global";
import CheckinStage, {
  DESIGN_H,
  DESIGN_W,
  DesignButton,
  Line,
  Piece,
  u,
} from "../components/CheckinStage";
import scan_frame from "../assets/checkin/scan_frame.png";
import title_success from "../assets/checkin/title_success.png";
import title_checked from "../assets/checkin/title_checked.png";
import title_invalid from "../assets/checkin/title_invalid.png";

// Luồng (T-F2KK): quét được mã → gọi thẳng /checkin/confirm (ghi check-in) → CHECK-IN THÀNH CÔNG
// / ĐÃ CHECK-IN (giờ lần đầu + khu). Mã không hợp lệ (T-DX5Z = A) → MÃ QR KHÔNG HỢP LỆ.
// Camera bắn cùng 1 mã nhiều lần → khoá `busy` từ lúc quét tới khi bấm "Quay về":
// mỗi lần quét chỉ gọi confirm đúng 1 lần (không thì count_checkin tăng sai).
type ScanState = {
  result: CheckinResult;
  data: CheckinInfo | null;
};

// "FANZONE A" → "Fanzone A" (như design); VIP/VVIP giữ chữ hoa
export const formatArea = (area?: string | null) =>
  (area || "")
    .split(" ")
    .map((w) =>
      /^V+IP$/i.test(w) || w.length <= 1
        ? w.toUpperCase()
        : w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()
    )
    .join(" ");

const isVVIP = (data: CheckinInfo | null) =>
  (data?.area || "").toUpperCase() === "VVIP";

// Khung camera trong design INFOMATION FRAME (phần trắng): x 128..651, y 405..911
const CAMERA = { x: 128, y: 405, w: 524, h: 507 };

function Home() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const dataUser = useSelector(getUserData);
  const [scan, setScan] = useState<ScanState | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [cameraError, setCameraError] = useState("");
  const busy = useRef(false);

  useEffect(() => {
    if (!dataUser) navigate("/");
  }, [dataUser]);

  const handleExpired = () => {
    ToastProvider({
      type: "error",
      content: "Phiên đăng nhập hết hạn, vui lòng đăng nhập lại",
    });
    dispatch(logout());
    navigate("/");
  };

  const confirm = async (code: string) => {
    setIsLoading(true);
    // Chỉ mở khoá khi không ra màn kết quả (lỗi mạng / 401); có kết quả thì giữ khoá tới "Quay về"
    let unlock = true;
    try {
      const res: any = await Api.post(URL_API.CONFIRM, { code_id: code }, dataUser?.token);
      if (res.status === 401) {
        handleExpired();
        return;
      }
      const body = res.data || {};
      if (body.result === "invalid" || body.status === false || res.status === 422) {
        setScan({ result: "invalid", data: null });
        unlock = false;
      } else if (body.result) {
        setScan({ result: body.result, data: body.data });
        unlock = false;
      } else {
        throw new Error("Bad response");
      }
    } catch (e) {
      console.log("callApi", e);
      ToastProvider({
        type: "error",
        content: "Không kết nối được máy chủ, vui lòng thử lại",
      });
    } finally {
      setIsLoading(false);
      if (unlock) busy.current = false;
    }
  };

  const onScan = (codes: any[]) => {
    const raw = codes?.[0]?.rawValue;
    if (!raw || busy.current || scan) return;
    busy.current = true;
    confirm(String(raw).trim());
  };

  const handleBack = () => {
    setScan(null);
    setIsLoading(false);
    busy.current = false;
  };

  const data = scan?.data || null;
  const vvip = isVVIP(data);

  // Dòng "Quý khách <tên>" (design VVIP: baseline 707)
  const guestLine = (baseline: number) =>
    data?.full_name ? (
      <Line baseline={baseline} size={41.7} weight={700}>
        <span
          className="font-medium align-baseline"
          style={{ fontSize: u(22.9), marginRight: u(14) }}
        >
          Quý khách
        </span>
        <span className="checkin-neon-blue">{data.full_name}</span>
      </Line>
    ) : null;

  const areaLine = (baseline: number) => (
    <Line baseline={baseline} size={62.5} weight={700} className="checkin-neon-blue">
      {formatArea(data?.area)}
    </Line>
  );

  const renderResult = () => {
    if (!scan) return null;
    switch (scan.result) {
      case "success":
        return (
          <>
            <Piece src={title_success} x={50} y={365} w={680} alt="CHECK-IN THÀNH CÔNG" />
            <Line baseline={642} size={41.7}>
              Vào lúc {data?.time_checkin_text}
            </Line>
            {vvip ? (
              <>
                {guestLine(707)}
                {areaLine(799)}
              </>
            ) : (
              areaLine(771)
            )}
            <DesignButton label="Quay về" onClick={handleBack} />
          </>
        );
      case "checked_in":
        return (
          <>
            <Piece src={title_checked} x={50} y={410} w={680} alt="ĐÃ CHECK-IN" />
            <Line baseline={599} size={41.7}>
              Vào lúc {data?.time_checkin_text}
            </Line>
            {vvip ? (
              <>
                {guestLine(707)}
                {areaLine(799)}
              </>
            ) : (
              areaLine(771)
            )}
            <DesignButton label="Quay về" onClick={handleBack} />
          </>
        );
      default:
        return (
          <>
            <Piece src={title_invalid} x={20} y={410} w={740} alt="MÃ QR KHÔNG HỢP LỆ" />
            <DesignButton label="Quay về" onClick={handleBack} />
          </>
        );
    }
  };

  return (
    <CheckinStage>
      {dataUser && (
        <button
          type="button"
          onClick={() => navigate("/game/logout")}
          className="absolute right-0 top-0 bg-transparent border-0 text-white/70 cursor-pointer"
          style={{ fontSize: u(20), padding: u(18) }}
        >
          {dataUser.username} · Đăng xuất
        </button>
      )}

      {scan ? (
        renderResult()
      ) : (
        <>
          <div
            className="absolute overflow-hidden bg-black"
            style={{
              left: `${(CAMERA.x / DESIGN_W) * 100}%`,
              top: `${(CAMERA.y / DESIGN_H) * 100}%`,
              width: `${(CAMERA.w / DESIGN_W) * 100}%`,
              height: `${(CAMERA.h / DESIGN_H) * 100}%`,
              borderRadius: u(10),
            }}
          >
            <Scanner
              onScan={onScan}
              onError={(e: any) => setCameraError(e?.message || "Không mở được camera")}
              formats={["qr_code"]}
              constraints={{ facingMode: "environment" }}
              components={{ finder: false }}
              sound={false}
              styles={{
                container: { width: "100%", height: "100%" },
                video: { width: "100%", height: "100%", objectFit: "cover" },
              }}
            />
            {(isLoading || cameraError) && (
              <div
                className="absolute inset-0 flex items-center justify-center text-center bg-black/60 text-white"
                style={{ fontSize: u(26), padding: u(30) }}
              >
                {isLoading ? "Đang kiểm tra mã..." : cameraError}
              </div>
            )}
          </div>
          <Piece src={scan_frame} x={90} y={368} w={600} />
          <Line
            baseline={974}
            size={22.9}
            weight={400}
            style={{ letterSpacing: "0.06em" }}
          >
            Vui lòng quét mã để Check-in
          </Line>
        </>
      )}
    </CheckinStage>
  );
}

export default Home;
